import {
  AlertTriangle,
  Car,
  ClipboardList,
  FileText,
  Gavel,
  History,
  Receipt,
  Scale,
  UserX,
} from 'lucide-react';

import { formatarDataHora } from '@/nucleo/formato/datas';
import { cn } from '@/nucleo/utilitarios';
import { EmptyState } from '@/ui/EmptyState';
import { Spinner } from '@/ui/Spinner';
import { Badge } from '@/ui/base/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/ui/base/card';

import { useLinhaDoTempoDoCredenciado, type EventoDaLinhaDoTempo } from './detalheDados';

interface AbaLinhaDoTempoProps {
  credenciadoId: string;
}

const ICONE: Record<EventoDaLinhaDoTempo['tipo'], typeof History> = {
  INSCRICAO: ClipboardList,
  TERMO: FileText,
  OS: Car,
  MULTA: Receipt,
  OCORRENCIA: AlertTriangle,
  RECURSO: Scale,
  SUSPENSAO: UserX,
  DESCREDENCIAMENTO: Gavel,
};

const COR: Record<EventoDaLinhaDoTempo['tipo'], string> = {
  INSCRICAO: 'bg-muted text-foreground',
  TERMO: 'bg-primary/10 text-primary',
  OS: 'bg-info/10 text-info',
  MULTA: 'bg-destructive/10 text-destructive',
  OCORRENCIA: 'bg-warning/10 text-warning',
  RECURSO: 'bg-warning/10 text-warning',
  SUSPENSAO: 'bg-warning/10 text-warning',
  DESCREDENCIAMENTO: 'bg-destructive/10 text-destructive',
};

/**
 * Aba "Linha do tempo" — versão enxuta do `HistoricoUnificadoTab` do
 * protótipo: lá o evento vinha de uma RPC que juntava seis tabelas
 * (inscrição, termo, OS, multa, ocorrência, recurso); aqui é uma lista mockada
 * já unificada, mesma leitura visual.
 */
export function AbaLinhaDoTempo({ credenciadoId }: AbaLinhaDoTempoProps) {
  const { data: eventos, isLoading } = useLinhaDoTempoDoCredenciado(credenciadoId);

  if (isLoading) {
    return <Spinner size="sm" />;
  }

  if (!eventos || eventos.length === 0) {
    return (
      <EmptyState
        icon={History}
        title="Sem histórico consolidado"
        description="Eventos aparecerão aqui conforme atividades forem registradas."
      />
    );
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-sm">
          <History className="h-4 w-4" aria-hidden="true" /> Linha do tempo do credenciado
          <Badge variant="secondary" className="ml-auto text-xs">
            {eventos.length} eventos
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        <div className="before:bg-border relative space-y-3 before:absolute before:bottom-0 before:left-[15px] before:top-0 before:w-px">
          {eventos.map((evento) => {
            const Icon = ICONE[evento.tipo] ?? History;
            return (
              <div key={evento.id} className="relative pl-10">
                <div
                  className={cn(
                    'absolute left-0 top-1 flex h-8 w-8 items-center justify-center rounded-full',
                    COR[evento.tipo],
                  )}
                >
                  <Icon className="h-4 w-4" aria-hidden="true" />
                </div>
                <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                  <Badge variant="outline" className="text-[10px]">
                    {evento.tipo}
                  </Badge>
                  <span className="text-sm font-medium">{evento.titulo}</span>
                  <span className="text-muted-foreground ml-auto text-xs">
                    {formatarDataHora(evento.data_evento)}
                  </span>
                </div>
                {evento.descricao && (
                  <p className="text-muted-foreground mt-0.5 text-xs">{evento.descricao}</p>
                )}
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
